import { useMemo } from "react";
import { useParams } from "react-router";
import TableError from "@cle/components/TableError";
import {
  ReportStatus,
  type ReportingPeriod,
} from "@cle/models/reportingPeriod";
import { isCurrentPeriod } from "@/util/date";
import { useHentEfteruddannelsesperiode } from "@/hooks/useHentEfteruddannelsesperiode";
import type { EfteruddannelsesPeriodeDetail } from "@/interfaces/efteruddannelse";
import CurrentIndberetningDetailView from "./CurrentIndberetningDetailView";
import OpenIndberetningDetailView from "./OpenIndberetningDetailView";
import IndberetningDetailSkeleton from "./components/IndberetningDetailSkeleton";
import ReportedIndberetningDetailView from "./ReportedIndberetningDetailView";
import { resolveIndberetningDetailVariant } from "./resolveIndberetningDetailVariant";

const mapEfteruddannelsesPeriodeToReportingPeriod = (
  period: EfteruddannelsesPeriodeDetail,
): ReportingPeriod => ({
  id: period.id,
  deadline: period.frist ?? null,
  periodFromDate: period.periodeFraDato,
  periodToDate: period.periodeTilDato,
  periodToDateMinusOneDay: period.periodeTilDatoMinusEnDag,
  transferredToNextPeriod: period.lektionerOverfoeresTilNaeste?.sum ?? null,
  transferredFromPreviousPeriod: period.lektionerOverfoertTilDenne?.sum ?? null,
  reportedLessons: period.lektionerIndberettet,
  transferredFromPreviousLessons: period.lektionerOverfoertTilDenne,
  transferredToNextLessons: period.lektionerOverfoeresTilNaeste,
  hasBeenReported: period.indsendt != null,
  hasOpenReport: period.harAabenIndberetning,
  openReportId: period.harAabenIndberetning ? period.indberetningId : null,
  report:
    period.indsendt != null
      ? {
          id: period.id,
          status: ReportStatus.COMPLETED,
          uploaded_at: new Date(period.indsendt),
        }
      : null,
});

const IndberetningDetailView = () => {
  const { id } = useParams<{ id: string }>();
  const {
    data: efteruddannelsesPeriode,
    isError,
    isLoading,
  } = useHentEfteruddannelsesperiode(id);
  const reportingPeriod = useMemo(
    () =>
      efteruddannelsesPeriode
        ? mapEfteruddannelsesPeriodeToReportingPeriod(efteruddannelsesPeriode)
        : undefined,
    [efteruddannelsesPeriode],
  );

  if (isLoading) {
    return <IndberetningDetailSkeleton />;
  }

  if (isError) {
    return (
      <div className="cle-report-detail">
        <TableError message="Indberetningen kunne ikke indlæses lige nu." />
      </div>
    );
  }

  if (!reportingPeriod) {
    return (
      <div className="cle-report-detail">
        <div className="alert alert-info mb-0" role="status">
          Indberetningen blev ikke fundet.
        </div>
      </div>
    );
  }

  const variant = resolveIndberetningDetailVariant(reportingPeriod);

  if (variant === "reported") {
    return <ReportedIndberetningDetailView reportingPeriod={reportingPeriod} />;
  }

  if (variant === "open") {
    // Key-reset: nyt udvalg pr. periode/indberetning i stedet for en
    // ref-styret nulstilling inde i viewet.
    return (
      <OpenIndberetningDetailView
        key={`${reportingPeriod.id}:${reportingPeriod.openReportId ?? ""}`}
        reportingPeriod={reportingPeriod}
      />
    );
  }

  return (
    <CurrentIndberetningDetailView
      isCurrentPeriod={isCurrentPeriod(
        reportingPeriod.periodFromDate,
        reportingPeriod.periodToDate,
      )}
      reportingPeriod={reportingPeriod}
    />
  );
};

export default IndberetningDetailView;
